import { BridgeUnavailableError, NfcWriteError, toErrorCode, type NfcPayload, type NfcWriter, type NfcWriterStatus } from './types'

/** Desktop NFC bridge (loopback only). Protocol: GET /v1/status, POST /v1/write. */
export const BRIDGE_URL = 'http://127.0.0.1:47321'
const STATUS_TIMEOUT_MS = 1500
const WRITE_TIMEOUT_MS = 60_000

interface BridgeStatus {
  version?: string
  readers?: { name: string; connected: boolean }[]
  tag?: { present?: boolean; uid?: string; type?: string; protected?: boolean | null } | null
}

/** fetch with a hard timeout that also honours the caller's signal. */
async function bridgeFetch(path: string, init: RequestInit, timeoutMs: number, signal?: AbortSignal): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  const onAbort = () => controller.abort()
  signal?.addEventListener('abort', onAbort)
  try {
    return await fetch(BRIDGE_URL + path, { ...init, signal: controller.signal, credentials: 'omit', cache: 'no-store' })
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  }
}

export const bridgeWriter: NfcWriter = {
  kind: 'bridge',

  async getStatus(signal) {
    let body: BridgeStatus
    try {
      const response = await bridgeFetch('/v1/status', { method: 'GET' }, STATUS_TIMEOUT_MS, signal)
      if (!response.ok) throw new BridgeUnavailableError()
      body = (await response.json()) as BridgeStatus
    } catch (error) {
      if (signal?.aborted) throw error
      throw error instanceof BridgeUnavailableError ? error : new BridgeUnavailableError()
    }
    if (!body || typeof body.version !== 'string' || !Array.isArray(body.readers)) throw new BridgeUnavailableError()
    if (!body.readers.some((r) => r.connected)) return { state: 'no_reader' } satisfies NfcWriterStatus
    if (!body.tag?.present) return { state: 'no_tag' } satisfies NfcWriterStatus
    return { state: 'tag_present', uid: body.tag.uid, protectedTag: body.tag.protected ?? null }
  },

  async write(payload: NfcPayload, signal) {
    let body: { ok?: boolean; uid?: string; already_protected?: boolean; code?: string; message?: string }
    try {
      const response = await bridgeFetch('/v1/write', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, WRITE_TIMEOUT_MS, signal)
      body = (await response.json()) as typeof body
    } catch (error) {
      if (signal?.aborted) throw error
      throw new NfcWriteError('BRIDGE_UNAVAILABLE')
    }
    if (body?.ok === true && typeof body.uid === 'string') return { uid: body.uid, alreadyProtected: body.already_protected === true }
    throw new NfcWriteError(toErrorCode(body?.code), body?.message)
  },
}
